import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { MenuComponent } from '../menu/menu/menu.component';
import { SDSColors } from '../../utils';
import { NgStyle } from '@angular/common';
import { TooltipService } from '../../services/tooltip.service';
import { SafeHtml } from '@angular/platform-browser';

@UntilDestroy()
@Component({
  selector: 'app-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss'],
  imports: [forwardRef(() => MenuComponent), NgStyle],
})
export class TooltipComponent implements OnInit, OnChanges {
  @Input() icon: string | SafeHtml = 'info_outline';
  @Input() iconColor = '';
  @Input() iconSize: string | number = 24;
  @Input() label = '';
  @Input() labelColor = '';
  @Input() labelMinWidth = '40px';
  @Input() iconFirst = false;
  @Input() width = '175px';
  @Input() backgroundColor: string = SDSColors.background;
  @Input() tooltipStyle: { [k: string]: string } = {};
  @Input() direction = 'rm';

  @ViewChild('menu')
  menu?: MenuComponent;

  _tooltipStyle: { [k: string]: string } = {};
  _labelStyle: { [k: string]: string } = {};
  _menuStyle: { [k: string]: string | number } = {};

  private subscription?: Subscription;

  constructor(private tooltipService: TooltipService) {}

  ngOnInit(): void {
    this._tooltipStyle = { width: this.width, padding: '16px', ...this.tooltipStyle };
    this.updateMenuStyle();
    this.updateLabelStyle();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tooltipStyle || changes.width) {
      this._tooltipStyle = { width: this.width, ...this.tooltipStyle };
    }
    if (changes.iconSize) {
      this.updateMenuStyle();
    }
    if (changes.labelMinWidth || changes.labelColor) {
      this.updateLabelStyle();
    }
  }

  onShowMenu(): void {
    // When this tooltip opens, any other open tooltip automatically closes because
    // every show and hide operation on any menu and submenu triggers the tooltip-service.
    this.subscription = this.tooltipService.hideAll$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.hide());
  }

  hide(): void {
    this.subscription?.unsubscribe();
    this.subscription = undefined;
    this.menu?.hide();
  }

  private updateMenuStyle() {
    const size = this.calculateIconSize(this.iconSize);
    this._menuStyle = { 'height.px': size };
  }

  private updateLabelStyle() {
    this._labelStyle = {
      color: this.labelColor,
      flex: `1 0 ${this.labelMinWidth}`,
    };
  }

  private calculateIconSize(size: string | number): number {
    if (typeof size === 'number') {
      return size;
    }
    switch (size) {
      case 'huge':
        return 56;
      case 'large':
        return 28;
      case 'small':
        return 18;
    }
    const value = parseInt(size, 10);
    if (isNaN(value)) {
      return 24;
    }
    return value;
  }
}
